'use client';

import { useMemo, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import FormControl from '@mui/material/FormControl';

import { Iconify } from 'src/components/iconify';
import { useDisplayTimezone } from 'src/components/settings/context';
import { usePopover, CustomPopover } from 'src/components/custom-popover';
import {
  TIMEZONE_CATALOG,
  findCountryForZone,
  getOffsetLabel,
  validateTimezone,
} from 'src/services/timezone-service';

/**
 * Header selector: country -> IANA zone (spec §4). The choice is a UI context only;
 * it changes how UTC instants are displayed and which zone new input is
 * interpreted in — it never rewrites stored timestamps (spec §21).
 */
export function TimezonePopover({ sx, ...other }) {
  const popover = usePopover();
  const { timezone, offsetLabel, setTimezone, resetToBrowserTimezone, browserTimezone, isBrowserDefault } =
    useDisplayTimezone();

  const [country, setCountry] = useState(() => findCountryForZone(timezone) ?? '');

  const zones = useMemo(() => TIMEZONE_CATALOG.find((c) => c.country === country)?.zones ?? [], [country]);

  const handleCountry = useCallback((event) => {
    const next = event.target.value;
    setCountry(next);
    const first = TIMEZONE_CATALOG.find((c) => c.country === next)?.zones?.[0]?.id;
    if (first && validateTimezone(first)) setTimezone(first);
  }, [setTimezone]);

  const handleZone = useCallback(
    (event) => {
      const tz = event.target.value;
      if (validateTimezone(tz)) setTimezone(tz);
    },
    [setTimezone]
  );

  const handleBrowser = useCallback(() => {
    resetToBrowserTimezone();
    setCountry(findCountryForZone(browserTimezone) ?? '');
  }, [resetToBrowserTimezone, browserTimezone]);

  return (
    <>
      <ButtonBase
        disableRipple
        onClick={popover.onOpen}
        aria-label="Viewing timezone"
        data-testid="timezone-popover-button"
        sx={{ py: 0.5, px: 1, gap: 0.75, borderRadius: 1, ...sx }}
        {...other}
      >
        <Iconify icon="solar:global-bold-duotone" width={22} sx={{ color: 'text.secondary' }} />
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
            Viewing timezone
          </Typography>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
            {timezone} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>({offsetLabel})</Box>
          </Typography>
        </Box>
        <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
      </ButtonBase>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'top-right' } }}
      >
        <Stack spacing={2} sx={{ p: 2, width: 320 }}>
          <Box>
            <Typography variant="subtitle2">Viewing / editing timezone</Typography>
            <Typography variant="caption" color="text.secondary">
              Times you enter are interpreted in this zone and every schedule is displayed in it.
              Changing it never modifies saved bookings.
            </Typography>
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel id="tz-country-label">Country</InputLabel>
            <Select labelId="tz-country-label" label="Country" value={country} onChange={handleCountry} data-testid="tz-country-select">
              {TIMEZONE_CATALOG.map((c) => (
                <MenuItem key={c.country} value={c.country}>
                  {c.country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth disabled={!country}>
            <InputLabel id="tz-zone-label">Timezone</InputLabel>
            <Select labelId="tz-zone-label" label="Timezone" value={zones.some((z) => z.id === timezone) ? timezone : ''} onChange={handleZone} data-testid="tz-zone-select">
              {zones.map((z) => (
                <MenuItem key={z.id} value={z.id}>
                  {z.label} ({getOffsetLabel(z.id)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
              Browser: {browserTimezone}
            </Typography>
            <Button
              size="small"
              variant={isBrowserDefault ? 'contained' : 'outlined'}
              onClick={handleBrowser}
              data-testid="tz-use-browser"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Use browser timezone
            </Button>
          </Stack>
        </Stack>
      </CustomPopover>
    </>
  );
}
